import {
  Address,
  AddressUtil,
  AgpSharedReadableAddressPipe,
  AustraliaUtil
} from '@liberty-financial/agp-shared';
import { GuidUtil } from '@liberty-financial/ng-core';

export type OpportunityLocalAddressContract = {
  unitNumber: string | null;
  streetName?: string;
  streetNumber: string | null;
  streetType?: string | null;
  suburb: {
    name: string;
    state: string;
    postcode: string;
  };
  country: string;
  googleLookup: {
    displayText: string | null;
    latitude: number | null;
    longitude: number | null;
    apiId: string | null;
  } | null;
};

export type OpportunityCurrentAddressContract = {
  address: OpportunityLocalAddressContract;
  startDate?: Date;
};

export type OpportunityInternationalAddressContract = {
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string;
};

export type OpportunityAddressSchemaContract =
  | { localAddress: OpportunityLocalAddressContract }
  | { internationalAddress: OpportunityInternationalAddressContract };

export type OpportunityAddressSchemaWithIdContract = {
  id: string;
} & OpportunityAddressSchemaContract;

export function addressToOpportunityAddressSchemaContract(
  address: Address | null
): OpportunityAddressSchemaContract | null {
  if (!address) {
    return null;
  }

  if (AddressUtil.isInternational(address)) {
    return {
      internationalAddress: {
        addressLine1: address.addressLine1 || '',
        addressLine2: address.addressLine2 || null,
        city: address.city || '',
        state: address.state,
        zip: address.postcode,
        country: address.country
      }
    };
  }

  if (AddressUtil.isAustralia(address)) {
    return {
      localAddress: {
        unitNumber: address.unitNumber || null,
        streetNumber: address.streetNumber || null,
        streetName: address.streetName || undefined,
        streetType: address.streetType || undefined,
        googleLookup: address.geo?.placeId
          ? {
              displayText: SparkAddressUtil.getDisplayTextFromAddress({
                ...address,
                state: AustraliaUtil.getStateInShortName(address.state) || ''
              }),
              latitude: address.geo?.lat || null,
              longitude: address.geo?.lng || null,
              apiId: address.geo?.placeId || null
            }
          : null,
        suburb: {
          name: address.suburb || '',
          state: AustraliaUtil.getStateInShortName(address.state) || '',
          postcode: address.postcode
        },
        country: address.country
      }
    };
  }

  return {
    localAddress: {
      unitNumber: address.unitNumber || null,
      streetNumber: address.streetNumber || null,
      streetName: address.streetName || undefined,
      streetType: address.streetType || undefined,
      googleLookup: address.geo?.placeId
        ? {
            displayText: SparkAddressUtil.getDisplayTextFromAddress(address),
            latitude: address.geo?.lat || null,
            longitude: address.geo?.lng || null,
            apiId: address.geo?.placeId || null
          }
        : null,
      suburb: {
        name: address.suburb || '',
        state: address.city || '',
        postcode: address.postcode
      },
      country: address.country
    }
  };
}

export function opportunityAddressSchemaContractToAddress(
  address?:
    | OpportunityAddressSchemaContract
    | OpportunityAddressSchemaWithIdContract
    | null
): Address | null {
  if (!address) {
    return null;
  }

  const id = 'id' in address ? address.id : GuidUtil.generate();

  if ('internationalAddress' in address) {
    return {
      id,
      addressLine1: address.internationalAddress.addressLine1 || '',
      addressLine2: address.internationalAddress.addressLine2 || undefined,
      city: address.internationalAddress.city || '',
      state: address.internationalAddress.state || '',
      postcode: address.internationalAddress.zip || '',
      country: address.internationalAddress.country
    };
  }
  const country = address.localAddress.country;

  if (country === 'Australia') {
    return {
      id,
      unitNumber: address.localAddress.unitNumber || undefined,
      streetNumber: address.localAddress.streetNumber || undefined,
      streetName: address.localAddress.streetName || '',
      streetType: address.localAddress.streetType || '',
      geo: address.localAddress.googleLookup?.apiId
        ? {
            lat: address.localAddress.googleLookup?.latitude || 0,
            lng: address.localAddress.googleLookup?.longitude || 0,
            placeId: address.localAddress.googleLookup?.apiId || ''
          }
        : undefined,
      suburb: address.localAddress.suburb.name || '',
      state:
        AustraliaUtil.getStateInLongName(address.localAddress.suburb.state) ||
        '',
      postcode: address.localAddress.suburb.postcode,
      country: 'Australia'
    };
  }

  return {
    id,
    unitNumber: address.localAddress.unitNumber || undefined,
    streetNumber: address.localAddress.streetNumber || undefined,
    streetName: address.localAddress.streetName || '',
    streetType: address.localAddress.streetType || '',
    geo: address.localAddress.googleLookup?.apiId
      ? {
          lat: address.localAddress.googleLookup?.latitude || 0,
          lng: address.localAddress.googleLookup?.longitude || 0,
          placeId: address.localAddress.googleLookup?.apiId || ''
        }
      : undefined,
    suburb: address.localAddress.suburb.name || '',
    city: address.localAddress.suburb.state,
    postcode: address.localAddress.suburb.postcode,
    country: 'New Zealand'
  };
}

export class SparkAddressUtil {
  private static pipe = new AgpSharedReadableAddressPipe();

  public static getDisplayTextFromAddress(address?: Address | null) {
    return SparkAddressUtil.pipe.transform(address);
  }

  public static getDisplayTextFromOpportunityAddressContract(
    address?:
      | OpportunityAddressSchemaContract
      | OpportunityAddressSchemaWithIdContract
      | null
  ) {
    return SparkAddressUtil.getDisplayTextFromAddress(
      opportunityAddressSchemaContractToAddress(address)
    );
  }
}
